import React, { useState, useEffect, useMemo } from "react";
import { NavLink, Routes, Route, useParams, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { admin } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import PageHead from "@/components/PageHead";
import { AlertBanner, AlertHistory, RecoveryPanel } from "@/components/admin/DeliveryRecovery";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, AlertTriangle, RefreshCcw, ShieldCheck } from "lucide-react";

// ---------- Route protection ----------
export const AdminProtected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.is_demo || !user.is_admin) return <Navigate to="/dashboard" replace />;
  return children;
};

// ---------- Layout ----------
const AdminNav = () => (
  <nav
    className="border-b border-white/10 bg-vault-950/80 backdrop-blur-md sticky top-16 z-30"
    data-testid="admin-nav"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10 flex gap-6 overflow-x-auto">
      {[
        { to: "/admin", label: "Overview", end: true, tid: "nav-overview" },
        { to: "/admin/users", label: "Users", tid: "nav-users" },
        { to: "/admin/delivery", label: "Delivery Health", tid: "nav-delivery" },
        { to: "/admin/demo", label: "Demo", tid: "nav-demo" },
        { to: "/admin/audit", label: "Audit Log", tid: "nav-audit" },
      ].map((l) => (
        <NavLink
          key={l.to}
          to={l.to}
          end={l.end}
          data-testid={l.tid}
          className={({ isActive }) =>
            `label-mono py-4 border-b-2 transition-colors whitespace-nowrap ${
              isActive
                ? "border-terracotta text-bone"
                : "border-transparent text-white/50 hover:text-bone"
            }`
          }
        >
          {l.label}
        </NavLink>
      ))}
    </div>
  </nav>
);

const AdminShell = ({ children }) => (
  <div data-testid="admin-page" className="min-h-screen">
    <div className="max-w-7xl mx-auto px-6 md:px-10 pt-8">
      <div className="flex items-center gap-3">
        <ShieldCheck className="w-5 h-5 text-terracotta" strokeWidth={1.5} />
        <p className="label-mono text-terracotta">CHRONOVAULTS · ADMIN CONSOLE</p>
      </div>
    </div>
    <AdminNav />
    <AlertBanner />
    <main className="max-w-7xl mx-auto px-6 md:px-10 py-8">{children}</main>
  </div>
);

// ---------- Small primitives ----------
const StatCard = ({ label, value, hint, testid }) => (
  <div className="border border-white/10 bg-vault-900/40 p-5" data-testid={testid}>
    <p className="label-mono text-white/40 text-[10px]">{label}</p>
    <p className="font-serif text-3xl text-bone mt-3">{value ?? "—"}</p>
    {hint ? <p className="text-xs text-white/40 mt-2">{hint}</p> : null}
  </div>
);

const Badge = ({ tone = "muted", children }) => {
  const tones = {
    muted: "bg-white/5 text-white/60 border-white/10",
    ok: "bg-emerald-500/10 text-emerald-300 border-emerald-500/20",
    warn: "bg-terracotta/10 text-terracotta border-terracotta/30",
    info: "bg-white/10 text-bone border-white/15",
  };
  return (
    <span className={`px-2 py-0.5 text-[10px] label-mono border rounded-sm ${tones[tone] || tones.muted}`}>
      {children}
    </span>
  );
};

const useAsync = (fn, deps = []) => {
  const [state, setState] = useState({ data: null, loading: true, error: null });
  const refresh = () => {
    setState((s) => ({ ...s, loading: true }));
    fn()
      .then((data) => setState({ data, loading: false, error: null }))
      // eslint-disable-next-line
      .catch((err) => setState({ data: null, loading: false, error: err?.response?.data?.detail || err.message }));
  };
  useEffect(() => {
    refresh();
    // eslint-disable-next-line
  }, deps);
  return { ...state, refresh };
};

// ---------- Overview ----------
const Overview = () => {
  const { data, loading, error } = useAsync(() => admin.overview());
  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-terracotta" />;
  if (error) return <p className="text-terracotta">{String(error)}</p>;

  const u = data.users, c = data.capsules, held = data.credits_held_by_users_excl_demo, ce = data.credit_events_since_cutover;
  return (
    <div className="space-y-8" data-testid="overview-body">
      <section>
        <p className="label-mono mb-3">Users</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total" value={u.total} testid="stat-users-total" />
          <StatCard label="Signup · 7d" value={u.signup_last_7d} hint={u.missing_created_at ? `${u.missing_created_at} legacy without created_at` : null} testid="stat-signup-7d" />
          <StatCard label="Signup · 30d" value={u.signup_last_30d} testid="stat-signup-30d" />
          <StatCard label="Free plan" value={u.free_plan} testid="stat-free" />
          <StatCard label="Subscribers" value={u.subscribers_local_db_view} hint="Local DB view (webhook-synced)" testid="stat-subscribers" />
          <StatCard label="Demo users" value={u.demo} testid="stat-demo" />
          <StatCard label="With ≥1 capsule" value={u.with_at_least_one_capsule} testid="stat-active-creators" />
        </div>
      </section>

      <section>
        <p className="label-mono mb-3">Capsules</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total" value={c.total} testid="stat-caps-total" />
          <StatCard label="Scheduled" value={c.scheduled} testid="stat-caps-scheduled" />
          <StatCard label="Released" value={c.released} testid="stat-caps-released" />
          <StatCard label="Overdue" value={c.overdue} testid="stat-caps-overdue" />
          <StatCard label="Notification failed" value={c.notification_failed} testid="stat-caps-notif-failed" />
          <StatCard
            label="Recipient verified / access granted"
            value={c.recipient_verified_access_granted}
            hint="NOT proof the recipient watched the video"
            testid="stat-caps-verified"
          />
        </div>
      </section>

      <section>
        <p className="label-mono mb-3">Credits currently held by users (excludes demo)</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Free (signup)" value={held.free} testid="stat-held-free" />
          <StatCard label="Paygo (paid)" value={held.paygo} testid="stat-held-paygo" />
          <StatCard label="Promo (admin)" value={held.promo} testid="stat-held-promo" />
          <StatCard label="Subscription (retired)" value={held.subscription} testid="stat-held-sub" />
        </div>
      </section>

      <section>
        <p className="label-mono mb-3">Credit events since cutover</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard label="Purchased credits" value={ce.purchased_credits} testid="stat-purchased" />
          <StatCard label="Promo granted" value={ce.promo_granted} testid="stat-promo-granted" />
          <StatCard label="Promo deducted" value={ce.promo_deducted} testid="stat-promo-deducted" />
        </div>
        <p className="text-xs text-white/40 mt-3">{ce.note}</p>
      </section>
    </div>
  );
};

// ---------- Users list ----------
const UsersList = () => {
  const [q, setQ] = useState("");
  const [plan, setPlan] = useState("");
  const [isDemo, setIsDemo] = useState("");
  const nav = useNavigate();
  const params = useMemo(() => {
    const p = { limit: 50 };
    if (q.length >= 2) p.q = q;
    if (plan) p.plan = plan;
    if (isDemo) p.is_demo = isDemo === "true";
    return p;
  }, [q, plan, isDemo]);
  const { data, loading } = useAsync(() => admin.users(params), [JSON.stringify(params)]);

  return (
    <div data-testid="users-body">
      <div className="flex flex-wrap gap-3 mb-6 items-end">
        <div className="flex-1 min-w-[220px]">
          <Label className="label-mono text-[10px] mb-2 block">Search</Label>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="email or user id (≥2 chars)" data-testid="users-search" className="h-10" />
        </div>
        <div>
          <Label className="label-mono text-[10px] mb-2 block">Plan</Label>
          <Select value={plan} onValueChange={(v) => setPlan(v === "any" ? "" : v)}>
            <SelectTrigger className="h-10 w-40" data-testid="filter-plan"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any</SelectItem>
              <SelectItem value="free">Free</SelectItem>
              <SelectItem value="subscriber">Subscriber</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-mono text-[10px] mb-2 block">Demo</Label>
          <Select value={isDemo} onValueChange={(v) => setIsDemo(v === "any" ? "" : v)}>
            <SelectTrigger className="h-10 w-40" data-testid="filter-demo"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any</SelectItem>
              <SelectItem value="true">Demo only</SelectItem>
              <SelectItem value="false">Non-demo</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <Loader2 className="w-6 h-6 animate-spin text-terracotta" />
      ) : (
        <div className="overflow-x-auto border border-white/10">
          <table className="w-full text-sm">
            <thead className="bg-vault-950 text-white/50 label-mono text-[10px]">
              <tr>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Plan</th>
                <th className="text-left px-4 py-3">Free</th>
                <th className="text-left px-4 py-3">Paygo</th>
                <th className="text-left px-4 py-3">Promo</th>
                <th className="text-left px-4 py-3">Flags</th>
                <th className="text-left px-4 py-3">Signup</th>
              </tr>
            </thead>
            <tbody data-testid="users-tbody">
              {(data?.users || []).map((u) => (
                <tr
                  key={u.id}
                  onClick={() => nav(`/admin/users/${u.id}`)}
                  data-testid={`user-row-${u.id}`}
                  className="cursor-pointer border-t border-white/5 hover:bg-white/[0.02] transition-colors"
                >
                  <td className="px-4 py-3 text-bone">{u.email}</td>
                  <td className="px-4 py-3">
                    <Badge tone={u.plan === "subscriber" ? "info" : "muted"}>{u.plan}</Badge>
                  </td>
                  <td className="px-4 py-3 text-white/70">{u.credits.free}</td>
                  <td className="px-4 py-3 text-white/70">{u.credits.paygo}</td>
                  <td className="px-4 py-3 text-terracotta">{u.credits.promo}</td>
                  <td className="px-4 py-3 space-x-1">
                    {u.is_admin ? <Badge tone="warn">ADMIN</Badge> : null}
                    {u.is_demo ? <Badge tone="warn">DEMO</Badge> : null}
                  </td>
                  <td className="px-4 py-3 text-white/50 text-xs">{u.created_at?.slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(data?.users?.length ?? 0) === 0 ? (
            <p className="px-4 py-6 text-white/40 text-sm">No users match.</p>
          ) : null}
        </div>
      )}
      <p className="text-xs text-white/40 mt-3">
        Showing {data?.users?.length ?? 0} of {data?.total ?? 0}
      </p>
    </div>
  );
};

// ---------- User detail with grant/deduct + history ----------
const REASONS = [
  { value: "customer_service_goodwill", label: "Customer service goodwill" },
  { value: "beta_tester", label: "Beta tester" },
  { value: "promotional_campaign", label: "Promotional campaign" },
  { value: "service_recovery", label: "Service recovery" },
  { value: "manual_correction", label: "Manual correction" },
  { value: "other", label: "Other" },
];

const GrantModal = ({ open, onClose, userId, direction, onDone }) => {
  const [amount, setAmount] = useState(1);
  const [reason, setReason] = useState("customer_service_goodwill");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const submit = async () => {
    if (!confirm) {
      toast.error("Please tick the confirmation.");
      return;
    }
    setBusy(true);
    try {
      // Applied-intent marker model: client-generated idempotency key. Reusing
      // the same key with a different payload is rejected server-side with
      // 409 idempotency_key_payload_mismatch — so we regenerate per submit.
      const idempotency_key = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      const body = { amount: Number(amount), reason, note: note || undefined, idempotency_key };
      const res = direction === "grant" ? await admin.grantCredits(userId, body) : await admin.deductCredits(userId, body);
      toast.success(`${direction === "grant" ? "Granted" : "Deducted"} ${body.amount} promo credits · new balance ${res.after["credits.promo"]}${res.replay ? " (replay)" : ""}`);
      onDone();
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || d?.code || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-vault-950 border border-white/10 text-bone" data-testid={`${direction}-modal`}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">
            {direction === "grant" ? "Grant promotional credits" : "Deduct promotional credits"}
          </DialogTitle>
          <DialogDescription className="text-white/50 text-sm mt-2">
            Adjusts the user's <span className="text-terracotta">promo</span> pool only. Never touches paid credits or Stripe.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-3">
          <div>
            <Label className="label-mono text-[10px] mb-2 block">Amount</Label>
            <Input type="number" min={1} max={1000} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="grant-amount" className="h-10" />
          </div>
          <div>
            <Label className="label-mono text-[10px] mb-2 block">Reason</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="h-10" data-testid="grant-reason"><SelectValue /></SelectTrigger>
              <SelectContent>
                {REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-mono text-[10px] mb-2 block">Note (optional)</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="grant-note" maxLength={500} />
          </div>
          <label className="flex items-start gap-3 text-sm text-white/70 cursor-pointer">
            <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} data-testid="grant-confirm" className="mt-1" />
            I confirm this promotional adjustment and understand it will be logged in the audit trail.
          </label>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} className="h-10 border-white/20 bg-transparent text-bone hover:bg-white/5">Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || !confirm}
            data-testid="grant-submit"
            className={`h-10 ${direction === "grant" ? "bg-terracotta hover:bg-terracotta/90" : "bg-red-500/80 hover:bg-red-500"} text-white`}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {direction === "grant" ? "Grant credits" : "Deduct credits"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const UserDetail = () => {
  const { id } = useParams();
  const { data, loading, refresh } = useAsync(() => admin.user(id), [id]);
  const events = useAsync(() => admin.userCreditEvents(id, { limit: 100 }), [id]);
  const [grantOpen, setGrantOpen] = useState(false);
  const [deductOpen, setDeductOpen] = useState(false);

  if (loading || !data) return <Loader2 className="w-6 h-6 animate-spin text-terracotta" />;
  const u = data.user;

  return (
    <div data-testid="user-detail-body" className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="label-mono text-white/50 text-[10px]">User</p>
          <p className="font-serif text-3xl text-bone mt-2">{u.email}</p>
          <p className="text-xs text-white/40 mt-1">ID: {u.id}</p>
          <div className="flex gap-2 mt-3">
            <Badge tone={u.plan === "subscriber" ? "info" : "muted"}>{u.plan}</Badge>
            {u.is_admin ? <Badge tone="warn">ADMIN</Badge> : null}
            {u.is_demo ? <Badge tone="warn">DEMO</Badge> : null}
            {u.subscription?.status ? <Badge tone="info">sub: {u.subscription.status}</Badge> : null}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setGrantOpen(true)}
            disabled={u.is_demo}
            data-testid="btn-grant"
            className="h-10 bg-terracotta hover:bg-terracotta/90 text-white"
          >
            Grant promo credits
          </Button>
          <Button
            variant="outline"
            onClick={() => setDeductOpen(true)}
            disabled={u.is_demo || (u.credits?.promo ?? 0) <= 0}
            data-testid="btn-deduct"
            className="h-10 border-white/20 bg-transparent text-bone hover:bg-white/5"
          >
            Deduct
          </Button>
        </div>
      </div>

      <section className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard label="Free" value={u.credits.free} testid="uc-free" />
        <StatCard label="Paygo" value={u.credits.paygo} testid="uc-paygo" />
        <StatCard label="Promo" value={u.credits.promo} testid="uc-promo" />
        <StatCard label="Subscription" value={u.credits.subscription} testid="uc-sub" />
        <StatCard label="Demo" value={u.credits.demo} testid="uc-demo" />
      </section>

      <section className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard label="Capsules · total" value={data.capsule_counts.total} testid="uc-cap-total" />
        <StatCard label="Scheduled" value={data.capsule_counts.scheduled} testid="uc-cap-scheduled" />
        <StatCard label="Released" value={data.capsule_counts.released} testid="uc-cap-released" />
      </section>

      <section>
        <p className="label-mono mb-3">Credit history</p>
        <p className="text-xs text-white/40 mb-3">{data.notes.last_activity_disclaimer}</p>
        <div className="border border-white/10 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-vault-950 text-white/50 label-mono text-[10px]">
              <tr>
                <th className="text-left px-4 py-2">When</th>
                <th className="text-left px-4 py-2">Type</th>
                <th className="text-left px-4 py-2">Pool</th>
                <th className="text-left px-4 py-2">Amount</th>
                <th className="text-left px-4 py-2">Reason</th>
                <th className="text-left px-4 py-2">Note</th>
              </tr>
            </thead>
            <tbody data-testid="credit-events-tbody">
              {(events.data?.events || []).map((e) => (
                <tr key={e.id} className="border-t border-white/5">
                  <td className="px-4 py-2 text-white/60 text-xs">{e.created_at?.slice(0, 19)}</td>
                  <td className="px-4 py-2"><Badge tone={e.type.startsWith("admin") ? "warn" : e.type.startsWith("capsule_refund") ? "ok" : "muted"}>{e.type}</Badge></td>
                  <td className="px-4 py-2 text-white/70">{e.pool}</td>
                  <td className={`px-4 py-2 font-mono ${e.amount > 0 ? "text-emerald-300" : "text-terracotta"}`}>{e.amount > 0 ? `+${e.amount}` : e.amount}</td>
                  <td className="px-4 py-2 text-white/60">{e.reason || "—"}</td>
                  <td className="px-4 py-2 text-white/50 text-xs">{e.note || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(events.data?.events?.length ?? 0) === 0 ? (
            <p className="px-4 py-6 text-white/40 text-sm">No credit events for this user.</p>
          ) : null}
        </div>
      </section>

      <GrantModal open={grantOpen} onClose={() => setGrantOpen(false)} userId={id} direction="grant" onDone={() => { refresh(); events.refresh(); }} />
      <GrantModal open={deductOpen} onClose={() => setDeductOpen(false)} userId={id} direction="deduct" onDone={() => { refresh(); events.refresh(); }} />
    </div>
  );
};

// ---------- Delivery Health ----------
const Delivery = () => {
  const [status, setStatus] = useState("");
  const [failedOnly, setFailedOnly] = useState(false);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const params = useMemo(() => {
    const p = { limit: 100 };
    if (status) p.status = status;
    if (failedOnly) p.notification_failed = true;
    if (overdueOnly) p.overdue = true;
    return p;
  }, [status, failedOnly, overdueOnly]);
  const { data, loading, refresh } = useAsync(() => admin.capsules(params), [JSON.stringify(params)]);

  const retry = async (id) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm("Retry notifications for this capsule? Only failed channels will be re-sent — no duplicate release.")) return;
    try {
      await admin.retryNotifications(id);
      toast.success("Retry queued");
      refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Retry failed");
    }
  };

  return (
    <div data-testid="delivery-body">
      <RecoveryPanel />
      <AlertHistory />
      <div className="flex flex-wrap gap-3 mb-6 items-end">
        <div>
          <Label className="label-mono text-[10px] mb-2 block">Status</Label>
          <Select value={status} onValueChange={(v) => setStatus(v === "any" ? "" : v)}>
            <SelectTrigger className="h-10 w-40" data-testid="filter-status"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="released">Released</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-2 text-sm text-white/70 h-10">
          <input type="checkbox" checked={overdueOnly} onChange={(e) => setOverdueOnly(e.target.checked)} data-testid="filter-overdue" />
          Overdue only
        </label>
        <label className="flex items-center gap-2 text-sm text-white/70 h-10">
          <input type="checkbox" checked={failedOnly} onChange={(e) => setFailedOnly(e.target.checked)} data-testid="filter-notif-failed" />
          Notification failed
        </label>
      </div>

      {loading ? (
        <Loader2 className="w-6 h-6 animate-spin text-terracotta" />
      ) : (
        <div className="overflow-x-auto border border-white/10">
          <table className="w-full text-sm">
            <thead className="bg-vault-950 text-white/50 label-mono text-[10px]">
              <tr>
                <th className="text-left px-4 py-3">Capsule</th>
                <th className="text-left px-4 py-3">Owner</th>
                <th className="text-left px-4 py-3">Release at</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">SMS</th>
                <th className="text-left px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody data-testid="delivery-tbody">
              {(data?.capsules || []).map((c) => {
                const failed = c.status === "released" && (c.notification_email_ok === false || c.notification_sms_ok === false);
                return (
                  <tr key={c.id} className="border-t border-white/5">
                    <td className="px-4 py-3 text-bone text-xs">
                      <div className="text-bone">{c.title}</div>
                      <div className="text-white/40 font-mono text-[10px]">{c.id.slice(0, 12)}</div>
                    </td>
                    <td className="px-4 py-3 text-white/60 text-xs">{c.owner_email || c.user_id}</td>
                    <td className="px-4 py-3 text-white/60 text-xs">{c.release_at?.slice(0, 19)?.replace("T", " ")}</td>
                    <td className="px-4 py-3"><Badge tone={c.status === "released" ? "ok" : "muted"}>{c.status}</Badge></td>
                    <td className="px-4 py-3">
                      {c.notification_email_ok === true ? <Badge tone="ok">ok</Badge>
                        : c.notification_email_ok === false ? <Badge tone="warn">fail</Badge>
                        : <Badge tone="muted">—</Badge>}
                    </td>
                    <td className="px-4 py-3">
                      {c.notification_sms_ok === true ? <Badge tone="ok">ok</Badge>
                        : c.notification_sms_ok === false ? <Badge tone="warn">fail</Badge>
                        : <Badge tone="muted">—</Badge>}
                    </td>
                    <td className="px-4 py-3">
                      {failed ? (
                        <Button variant="outline" onClick={() => retry(c.id)} data-testid={`retry-${c.id}`}
                          className="h-8 border-white/20 bg-transparent text-bone hover:bg-white/5 text-xs">
                          <RefreshCcw className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} /> Retry
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {(data?.capsules?.length ?? 0) === 0 ? (
            <p className="px-4 py-6 text-white/40 text-sm">No capsules match.</p>
          ) : null}
        </div>
      )}
      <p className="text-xs text-white/40 mt-3">Message text and video content are never surfaced here.</p>
    </div>
  );
};

// ---------- Demo ----------
const DemoTab = () => {
  const { data, loading, refresh } = useAsync(() => admin.demoStatus());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  if (loading || !data) return <Loader2 className="w-6 h-6 animate-spin text-terracotta" />;
  if (!data.demo_configured) {
    return <p className="text-white/50">No demo account is currently configured.</p>;
  }
  const doReset = async () => {
    setBusy(true);
    try {
      await admin.demoReset();
      toast.success("Demo account reset");
      setConfirmOpen(false);
      refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Reset failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="demo-body" className="space-y-6">
      <div>
        <p className="label-mono text-white/50 text-[10px]">Demo user</p>
        <p className="font-serif text-2xl text-bone mt-2">{data.email}</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Credits · demo pool" value={data.credits_demo} testid="demo-credits" />
        <StatCard label="Capsules" value={data.capsule_count} testid="demo-capsules" />
        <StatCard label="Last reset" value={data.demo_last_reset_at?.slice(0, 16) || "never"} testid="demo-last-reset" />
        <StatCard label="Seeded at" value={data.demo_seeded_at?.slice(0, 16) || "—"} testid="demo-seeded" />
      </div>
      <Button onClick={() => setConfirmOpen(true)} data-testid="btn-demo-reset"
        className="h-11 bg-terracotta hover:bg-terracotta/90 text-white">
        Reset demo account
      </Button>
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="bg-vault-950 border border-white/10 text-bone" data-testid="demo-confirm-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Reset the demo account?</DialogTitle>
            <DialogDescription className="text-white/60 text-sm mt-2 leading-relaxed">
              This deletes every demo capsule + verification + reservation, then restores the demo credit balance to the configured default.
              This action is logged in the admin audit trail. Real customer accounts are never touched.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} className="h-10 border-white/20 bg-transparent text-bone hover:bg-white/5">Cancel</Button>
            <Button onClick={doReset} disabled={busy} data-testid="btn-demo-reset-confirm"
              className="h-10 bg-terracotta hover:bg-terracotta/90 text-white">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Reset now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------- Audit ----------
const Audit = () => {
  const [action, setAction] = useState("");
  const [targetUser, setTargetUser] = useState("");
  const params = useMemo(() => {
    const p = { limit: 100 };
    if (action) p.action = action;
    if (targetUser.length >= 2) p.target_user_id = targetUser;
    return p;
  }, [action, targetUser]);
  const { data, loading } = useAsync(() => admin.audit(params), [JSON.stringify(params)]);

  return (
    <div data-testid="audit-body">
      <div className="flex flex-wrap gap-3 mb-6 items-end">
        <div>
          <Label className="label-mono text-[10px] mb-2 block">Action</Label>
          <Select value={action} onValueChange={(v) => setAction(v === "any" ? "" : v)}>
            <SelectTrigger className="h-10 w-64" data-testid="audit-filter-action"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any</SelectItem>
              <SelectItem value="admin_grant_promo_credits">Grant promo credits</SelectItem>
              <SelectItem value="admin_deduct_promo_credits">Deduct promo credits</SelectItem>
              <SelectItem value="admin_retry_notifications">Retry notifications</SelectItem>
              <SelectItem value="admin_demo_reset">Demo reset</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-mono text-[10px] mb-2 block">Target user id</Label>
          <Input value={targetUser} onChange={(e) => setTargetUser(e.target.value)} placeholder="≥2 chars" data-testid="audit-filter-user" className="h-10 w-64" />
        </div>
      </div>
      {loading ? (
        <Loader2 className="w-6 h-6 animate-spin text-terracotta" />
      ) : (
        <div className="border border-white/10 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-vault-950 text-white/50 label-mono text-[10px]">
              <tr>
                <th className="text-left px-4 py-3">When</th>
                <th className="text-left px-4 py-3">Admin</th>
                <th className="text-left px-4 py-3">Action</th>
                <th className="text-left px-4 py-3">Target</th>
                <th className="text-left px-4 py-3">Before</th>
                <th className="text-left px-4 py-3">After</th>
                <th className="text-left px-4 py-3">Reason</th>
              </tr>
            </thead>
            <tbody data-testid="audit-tbody">
              {(data?.actions || []).map((a) => (
                <tr key={a.id} className="border-t border-white/5">
                  <td className="px-4 py-3 text-white/60 text-xs">{a.created_at?.slice(0, 19)}</td>
                  <td className="px-4 py-3 text-white/70 text-xs">{a.admin_email || a.admin_id?.slice(0, 8)}</td>
                  <td className="px-4 py-3"><Badge tone="info">{a.action}</Badge></td>
                  <td className="px-4 py-3 text-white/60 font-mono text-[10px]">{a.target_user_id || a.target_capsule_id || "—"}</td>
                  <td className="px-4 py-3 text-white/60 font-mono text-[10px]">{JSON.stringify(a.before_summary ?? {})}</td>
                  <td className="px-4 py-3 text-white/60 font-mono text-[10px]">{JSON.stringify(a.after_summary ?? {})}</td>
                  <td className="px-4 py-3 text-white/50 text-xs">{a.reason || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(data?.actions?.length ?? 0) === 0 ? (
            <p className="px-4 py-6 text-white/40 text-sm">No audit entries match.</p>
          ) : null}
        </div>
      )}
      <p className="text-xs text-white/40 mt-3">
        <AlertTriangle className="w-3.5 h-3.5 inline -mt-0.5 mr-1" strokeWidth={1.5} />
        Audit entries never contain private message text, video content, OTP codes, passwords or Stripe secrets.
      </p>
    </div>
  );
};

// ---------- Router ----------
export default function AdminPage() {
  return (
    <AdminShell>
      <PageHead title="Admin | ChronoVaults" noindex />
      <Routes>
        <Route index element={<Overview />} />
        <Route path="users" element={<UsersList />} />
        <Route path="users/:id" element={<UserDetail />} />
        <Route path="delivery" element={<Delivery />} />
        <Route path="demo" element={<DemoTab />} />
        <Route path="audit" element={<Audit />} />
      </Routes>
    </AdminShell>
  );
}
